"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Mark, type MarkName } from "@/components/mark";
import { seen } from "@/lib/seen";
import { haptic } from "@/lib/haptic";
import { InstallButton } from "@/components/install-button";
import { Wordmark } from "@/components/wordmark";
import styles from "./welcome.module.css";

/* Four posters rather than four cards. Each beat takes the whole screen in one colour,
   the way a printed run of them would, and the type is the only thing on it. The first
   says what this is before it says anything clever, because someone who has just
   installed it does not yet know. */
type Beat = {
  key: string;
  /* Which of the four grounds this beat is printed on. */
  ground: "clay" | "paper" | "sage" | "ink";
  script?: string;
  title: string;
  /* Set in Caveat, on its own line, after the display type. */
  flourish?: string;
  body: string;
  mark?: MarkName;
};

const BEATS: Beat[] = [
  {
    key: "hello",
    ground: "clay",
    script: "Welcome to",
    title: "Hot Tip",
    body: "The going-out app for people who already live here.",
    mark: "sun",
  },
  {
    key: "city",
    ground: "paper",
    title: "San Diego,",
    flourish: "block by block",
    body: "More than six thousand independent places, from Oceanside to San Ysidro. No chains, ever.",
    mark: "wave",
  },
  {
    key: "one",
    ground: "sage",
    script: "Three taps and",
    title: "You get one place.",
    flourish: "never a list",
    body: "Open right now, near you, independently owned. Don't like it? Shuffle.",
    mark: "pin",
  },
  {
    key: "go",
    ground: "ink",
    script: "Then you",
    title: "Show up.",
    flourish: "and the stamp lands",
    body: "Your phone knows you're there. Nobody behind the counter has to do a thing.",
    mark: "shop",
  },
];

export function Welcome() {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const beat = BEATS[step];
  const last = step === BEATS.length - 1;

  function go(to: number) {
    if (to < 0 || to > BEATS.length - 1 || leaving) return;
    haptic.tap();
    setStep(to);
  }

  function finish() {
    if (leaving) return;
    haptic.land();
    seen.set("welcome");
    setLeaving(true);
    // Long enough for the panel to fall away, short enough not to be a wait.
    // Everything past here needs an account, so the pitch hands over to the sign up.
    setTimeout(() => router.push("/signin"), 320);
  }

  function skip() {
    seen.set("welcome");
    router.push("/signin");
  }

  // Swipe, because a full screen panel invites a thumb before it invites a button.
  const from = useRef<{ x: number; y: number } | null>(null);
  function down(e: React.PointerEvent) {
    from.current = { x: e.clientX, y: e.clientY };
  }
  function up(e: React.PointerEvent) {
    const start = from.current;
    from.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) (last ? finish() : go(step + 1));
    else go(step - 1);
  }

  useEffect(() => {
    function key(e: KeyboardEvent) {
      if (e.key === "ArrowRight") last ? finish() : go(step + 1);
      if (e.key === "ArrowLeft") go(step - 1);
    }
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  return (
    <section
      className={`${styles.welcome} ${styles[beat.ground]} ${leaving ? styles.leaving : ""}`}
      aria-labelledby="welcome-h"
      onPointerDown={down}
      onPointerUp={up}
    >
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <p className={styles.count} aria-hidden="true">
          {String(step + 1).padStart(2, "0")} <span>/ {String(BEATS.length).padStart(2, "0")}</span>
        </p>
        <button type="button" className={styles.skip} onClick={skip}>Skip</button>
      </header>

      {/* Keyed on the beat so the whole block replays its entrance each time. */}
      <div className={styles.stage} key={beat.key}>
        {beat.mark && (
          <span className={styles.mark} aria-hidden="true">
            <Mark name={beat.mark} size={64} color="currentColor" decorative />
          </span>
        )}
        {beat.script && <p className={styles.script}>{beat.script}</p>}
        <h1 id="welcome-h" className={styles.title}>
          {beat.title}
          {beat.flourish && <span className={styles.flourish}>{beat.flourish}</span>}
        </h1>
        <p className={styles.body}>{beat.body}</p>
      </div>

      <footer className={styles.foot}>
        <div className={styles.brand}>
          <Wordmark />
          <p className={styles.brandCity}>San Diego</p>
        </div>

        <ol className={styles.dots} aria-label={`Step ${step + 1} of ${BEATS.length}`}>
          {BEATS.map((b, i) => (
            <li key={b.key}>
              <button
                type="button"
                className={styles.dot}
                aria-current={i === step ? "step" : undefined}
                aria-label={b.title}
                onClick={() => go(i)}
              />
            </li>
          ))}
        </ol>

        <button type="button" className={styles.next} onClick={() => (last ? finish() : go(step + 1))}>
          {last ? "Start with your name" : "Next"}
        </button>

        {last && <InstallButton />}

        <p className={styles.fine}>Free, forever. A name and an email, nothing else.</p>
      </footer>
    </section>
  );
}
